import { InjectionToken } from '@angular/core';
import moment from 'moment';
import { Observable } from 'rxjs';
import { InitializableInterface } from '../../interfaces/initializable.interface';
import { AddressModel } from '../../models/complex/address.model';
import { TimeRecordingEntryModel } from '../../models/complex/time-recording-entry.model';
import { TimesheetAndExpensesP58Model } from '../../models/complex/timesheet-and-expenses-p58.model';
import { ExpenseModel } from '../../models/low-level/expense.model';
import { NotificationModel } from '../../models/low-level/notification.model';
import { TimesheetModel } from '../../models/low-level/timesheet.model';
import { TextModel } from '../../models/primitive/text.model';

/**
 * Public contract of the TimesheetAndExpensesService: the facade for reading and
 * writing the current/submitted timesheet, its notifications and expenses, and the
 * technician's stored addresses.
 *
 * Extends {@link InitializableInterface}, which contributes `isInitialized` and `init()`.
 */
export interface TimesheetAndExpensesInterface extends InitializableInterface {
    /** Stream of the current (in-progress) timesheet from the store. */
    selectCurrentTimesheet$(options?: { once: true }): Observable<TimesheetModel>;

    /** Persist a timesheet as the current timesheet (concurrency store + IndexedDB). */
    saveCurrentTimesheet$(timesheet: TimesheetModel): Observable<TimesheetModel>;

    /** Transform the timesheet-and-expenses form model and save it as the current timesheet. */
    saveTimesheetFormModel(timesheetAndExpenses: TimesheetAndExpensesP58Model): Observable<TimesheetModel | void>;

    /** Submit the timesheet to the backend, archive it as submitted, and start synchronization. */
    submit(model: TimesheetAndExpensesP58Model): Observable<void>;

    /** Stream of the most recently submitted timesheet from the store. */
    selectSubmittedTimesheet$(): Observable<TimesheetModel>;

    /** Stream of the current timesheet's start-of-work moment. */
    getStartOfWork$(): Observable<moment.Moment>;

    /** Update the current timesheet's start of work if it differs from the given value. */
    saveStartOfWork(newStartOfWork: moment.Moment): Observable<TimesheetModel>;

    /** Remove notifications whose confirmation was already submitted from the given timesheet. */
    deleteSubmittedNotificationInfoFromTimesheet(timesheet: Readonly<TimesheetModel>): Observable<TimesheetModel>;

    /** Save all timesheet information (expenses, distance, time-recording) for a notification. */
    saveTimesheetNotification(
        notification: NotificationModel,
        expenses: ExpenseModel[],
        timeRecordingEntries: TimeRecordingEntryModel[],
        distance: TextModel,
        isAfterHoursCallOut?: boolean,
    ): Observable<TimesheetModel>;

    /** Return the cached address set. */
    getAddresses(): AddressModel[];

    /** Persist the given address set to local storage and cache it. */
    saveAddresses(es: AddressModel[]): Observable<void>;

    /** Add or update the given addresses in the cached set and persist if changed. */
    addAddressesAndSave(newAddresses: AddressModel[]): Observable<void>;

    /** Make all cached addresses visible again and persist. */
    resetAddresses(): Observable<void>;

    /** Hide a single address and persist. */
    hideAddress(address: AddressModel): Observable<void>;

    /** Remove an address from local storage. */
    deleteAddress(address: AddressModel): Observable<void>;
}

/**
 * DI token for {@link TimesheetAndExpensesInterface}. Lets the app choose which
 * implementation to provide (legacy TimesheetAndExpensesService or the NgRx-based
 * TimesheetAndExpensesNgrxService) without consumers depending on a concrete class.
 */
export const TimesheetAndExpensesInstanceToken = new InjectionToken<TimesheetAndExpensesInterface>(
    'TimesheetAndExpensesInterface',
);
